import { Flex, VStack, FormRow, DataItem } from '@cad-arc';
import {
    ConsolidatedLocationView,
    CopyCallForm as CopyCallFormType,
    CopyCallOptions,
    CopyCallView,
} from '@mark43/cad-api';
import { useEffect, useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { AgencyTypeGlobal, assertAgencyTypeGlobal } from '../../common/global-attributes';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import {
    getMultiAgencyClassificationDtoSubdivisionKeyForDepth,
    getSubdivisionLabelKeyForAgencyTypeAndDepth,
    getSubdivisionOptionsKeyForAgencyTypeAndDepth,
    SUBDIVISION_DEPTHS,
    useAgencyOptions,
} from '../../event-panel/event-panel-form/use-agency-options';
import { FormInput } from '../../forms/integrated-form-controls/form-input';
import { FormLocationSelect } from '../../forms/integrated-form-controls/form-location-select';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { CopyOptionsForm } from './copy-options-form';
import {
    getSubdivisionKeyFromLocationEntityLink,
    useLocationPrefill,
    UseLocationPrefillArgs,
} from '../../location-select/use-location-prefill';
import { ClassificationStrategy } from '../../common/types';

interface CopyCallFormProps {
    formData: CopyCallView;
    form: UseFormReturn<CopyCallFormDisplayType>;
}

export type CopyCallFormDisplayType = Omit<
    CopyCallFormType,
    'consolidatedLocationView' | 'useCurrentDateTime'
> & {
    consolidatedLocationView?: ConsolidatedLocationView;
    useCurrentDateTime: 'true' | 'false';
};

function useCopyCallLocationPrefill(
    form: UseFormReturn<CopyCallFormDisplayType>,
    options: CopyCallOptions,
    agencyTypeGlobalAttrId: AgencyTypeGlobal
) {
    const locationPrefillArgs: UseLocationPrefillArgs<CopyCallFormDisplayType> = useMemo(() => {
        const args: UseLocationPrefillArgs<CopyCallFormDisplayType> = {
            form,
            agencyType: agencyTypeGlobalAttrId,
            fieldPaths: {
                location: 'consolidatedLocationView',
                agency: 'agencyProfileOption',
                dispatchArea: 'dispatchAreaOption',
            },
            options: {
                dispatchAreaOptions: options.dispatchAreaOptions,
                agencyProfileOptions: options.agencyProfileOptions,
                radioChannelOptions: options.radioChannelOptions,
            },
        };
        return args;
    }, [
        form,
        options.agencyProfileOptions,
        options.dispatchAreaOptions,
        options.radioChannelOptions,
        agencyTypeGlobalAttrId,
    ]);

    useLocationPrefill<CopyCallFormDisplayType>(locationPrefillArgs);
}

export function CopyCallForm(props: CopyCallFormProps): JSX.Element {
    const { form: formData, options, statics } = props.formData;
    const { control, reset } = props.form;
    const { agencyTypeGlobalAttrId } = statics;
    assertAgencyTypeGlobal(agencyTypeGlobalAttrId);

    useCopyCallLocationPrefill(props.form, options, agencyTypeGlobalAttrId);

    const enabledFeatures = useEnabledFeatures();

    const bootstrapState = useBootstrapState();

    const combinedSubdivisionOptionsBootstrapView =
        bootstrapState.subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView;
    const agencyIdToSubdivisionOptionsBootstrapView =
        bootstrapState.subdivisionOptionsBootstrapResponse
            ?.agencyIdToSubdivisionOptionsBootstrapView;

    const currentLocation = useWatch({ control, name: 'consolidatedLocationView' });
    const currentAgency = useWatch({ control, name: 'agencyProfileOption' });

    useEffect(
        function hydrateForm() {
            if (formData !== undefined) {
                reset(
                    {
                        ...formData,
                        useCurrentDateTime: formData.useCurrentDateTime === true ? 'true' : 'false',
                    },
                    {
                        keepDefaultValues: true,
                    }
                );
            }
        },
        [formData, reset]
    );

    const subdivisionsDisplay: { label: string; value: string | undefined }[] = useMemo(() => {
        const ret: { label: string; value: string | undefined }[] = [];
        const locationEntityLink = currentLocation?.entityLinks[0];
        const agencyType = agencyTypeGlobalAttrId;
        const multiAgencyClassificationDto = !currentAgency
            ? undefined
            : currentLocation?.multiAgencyClassificationDtos.find(
                  (classificationDto) => classificationDto.agencyId === currentAgency.value
              );
        if (
            !enabledFeatures.multiAgencySubdivisions &&
            combinedSubdivisionOptionsBootstrapView &&
            locationEntityLink
        ) {
            for (const subdivisionDepth of SUBDIVISION_DEPTHS) {
                const optionsForClassAndSubdivisionDepth =
                    combinedSubdivisionOptionsBootstrapView[
                        getSubdivisionOptionsKeyForAgencyTypeAndDepth(agencyType, subdivisionDepth)
                    ];
                if (!optionsForClassAndSubdivisionDepth) {
                    continue;
                } else {
                    const labelKey = getSubdivisionLabelKeyForAgencyTypeAndDepth(
                        agencyType,
                        subdivisionDepth
                    );
                    const subdivisionLabel = combinedSubdivisionOptionsBootstrapView[labelKey];
                    const locationEntityLinkSubdivisionValueKey =
                        getSubdivisionKeyFromLocationEntityLink({ agencyType, subdivisionDepth });
                    const valueLabel = optionsForClassAndSubdivisionDepth.find(
                        (option) =>
                            option.value ===
                            locationEntityLink[locationEntityLinkSubdivisionValueKey]
                    )?.label;
                    if (subdivisionLabel) {
                        ret.push({
                            label: subdivisionLabel,
                            value: valueLabel,
                        });
                    }
                }
            }
        } else if (
            enabledFeatures.multiAgencySubdivisions &&
            multiAgencyClassificationDto &&
            currentAgency &&
            agencyIdToSubdivisionOptionsBootstrapView
        ) {
            for (const subdivisionDepth of SUBDIVISION_DEPTHS) {
                const subdivisionConfigForAgencyId =
                    agencyIdToSubdivisionOptionsBootstrapView[currentAgency.value];
                if (!subdivisionConfigForAgencyId) return ret;
                const optionsForClassAndSubdivisionDepth =
                    subdivisionConfigForAgencyId[
                        getSubdivisionOptionsKeyForAgencyTypeAndDepth(agencyType, subdivisionDepth)
                    ];
                if (!optionsForClassAndSubdivisionDepth) {
                    continue;
                } else {
                    const labelKey = getSubdivisionLabelKeyForAgencyTypeAndDepth(
                        agencyType,
                        subdivisionDepth
                    );
                    const subdivisionLabel = subdivisionConfigForAgencyId[labelKey];
                    const multiAgencyClassificationDtoSubdivisionValueKey =
                        getMultiAgencyClassificationDtoSubdivisionKeyForDepth(subdivisionDepth);
                    const valueLabel = optionsForClassAndSubdivisionDepth.find(
                        (option) =>
                            option.value ===
                            multiAgencyClassificationDto[
                                multiAgencyClassificationDtoSubdivisionValueKey
                            ]
                    )?.label;
                    if (subdivisionLabel) {
                        ret.push({
                            label: subdivisionLabel,
                            value: valueLabel,
                        });
                    }
                }
            }
        }
        return ret;
    }, [
        currentLocation?.entityLinks,
        currentLocation?.multiAgencyClassificationDtos,
        agencyTypeGlobalAttrId,
        currentAgency,
        enabledFeatures.multiAgencySubdivisions,
        combinedSubdivisionOptionsBootstrapView,
        agencyIdToSubdivisionOptionsBootstrapView,
    ]);

    const agencyOptions = useAgencyOptions({
        agencyType: agencyTypeGlobalAttrId,
        baseOptions: options.agencyProfileOptions,
        location: currentLocation ?? null,
    });

    return (
        <VStack spacing="4" align="left">
            <FormSelect
                autoFocus={true}
                name="callForServiceOption"
                control={control}
                rules={{ required: 'Required' }}
                options={options.callForServiceOptions}
                label="Call For Service"
            />
            <FormRow>
                <FormLocationSelect
                    name="consolidatedLocationView"
                    control={control}
                    rules={{ required: 'Required' }}
                    label="Location"
                    classificationStrategy={
                        enabledFeatures.multiAgencySubdivisions
                            ? ClassificationStrategy.MULTI_AGENCY
                            : ClassificationStrategy.SINGLE_AGENCY
                    }
                    length="lg"
                />
                {!enabledFeatures.subpremiseSupport && (
                    <FormInput name="subPremise" control={control} label="Apt, Unit" />
                )}
            </FormRow>
            <Flex style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--arc-space-3)' }}>
                {subdivisionsDisplay.length > 0 ? (
                    subdivisionsDisplay.map((subdivisionDisplay, i) => (
                        <DataItem
                            dataKey={subdivisionDisplay.label}
                            key={i}
                            style={{
                                fontSize: 'var(--arc-fontSizes-xs)',
                            }}
                        >
                            {subdivisionDisplay.value}
                        </DataItem>
                    ))
                ) : (
                    <DataItem
                        dataKey="Subdivisions"
                        style={{
                            fontSize: 'var(--arc-fontSizes-xs)',
                        }}
                    >
                        {undefined}
                    </DataItem>
                )}
            </Flex>
            <VStack gap="4px" align="left">
                <FormRow>
                    <FormSelect
                        name="dispatchAreaOption"
                        control={control}
                        rules={{ required: 'Required' }}
                        label={`${statics.eventClass} D-Area`}
                        options={options[`dispatchAreaOptions`]}
                        data-testid="dispatchAreaSelect"
                        length="sm"
                    />
                    <FormSelect
                        name="agencyProfileOption"
                        control={control}
                        rules={{ required: 'Required' }}
                        label={`${statics.eventClass} Agency`}
                        data-testid="agencySelect"
                        options={agencyOptions}
                        length="md"
                    />
                </FormRow>
                {enabledFeatures.radioChannels && (
                    <FormSelect
                        name="radioChannelOption"
                        control={control}
                        label={`${statics.eventClass} Primary Radio`}
                        options={options.radioChannelOptions}
                        data-testid="radioChannelSelect"
                        length="md"
                    />
                )}
            </VStack>

            <CopyOptionsForm control={control} />
        </VStack>
    );
}
